import CloseButton from '@/components/Modal/common/CloseButton';
import ModalBody from '@/components/Modal/common/ModalBody';
import { useConversionBreakdown } from '@/hooks/useConversionBreakdown';
import {
  CircularProgress,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { dspLabel } from '../../ReportingV2/format';
import type { DashboardFilters } from '../lib/dashboardFilters';

/** Friendly names for the filter keys EVENT-sourced breakdowns can drop —
 *  EVENT only carries day/hour/dsp/advertiser; nothing campaign-scoped. */
export const IGNORED_FILTER_LABELS: Record<string, string> = {
  campaign_ids: 'Campaign',
  line_item_ids: 'Line item',
  // The dashboard's Campaign / Line Item picks travel as ribeye ids (#4614).
  ribeye_campaign_ids: 'Campaign',
  ribeye_line_item_ids: 'Line item',
  creative_ids: 'Creative',
  channel_ids: 'Channel',
  device_ids: 'Device',
  product_ids: 'Product',
  publishers: 'Publisher',
};

interface IProps {
  open: boolean;
  onClose: () => void;
  filters: DashboardFilters | null;
  /**
   * The KPI widget's own already-correct aggregate total (a real
   * `metrics: ['conversions']` query, uncapped by this modal's 500-row
   * breakdown page). Shown as "Total" instead of summing the breakdown rows,
   * which would silently undercount if more than 500 distinct actions fired.
   */
  total: number | null;
}

const ConversionBreakdownModal = ({ open, onClose, filters, total }: IProps) => {
  const { rows, loading, error, ignoredFilters } = useConversionBreakdown(open ? filters : null);
  const ignoredFilterNames = ignoredFilters.map(key => IGNORED_FILTER_LABELS[key] ?? key);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalBody className='!w-[600px] !max-w-[95vw]'>
        <p className='pl-4 pt-2 mb-1 text-xl font-semibold'>Conversion actions</p>
        <p className='pl-4 mb-6 text-sm text-[#84818A]'>
          Which named conversion actions fired, and how many times, for the selected filters. This is action-level
          detail only — it isn't broken down by campaign, since {dspLabel('beeswax')}'s own conversion-pixel data
          doesn't carry campaign identity.
        </p>
        <div className='absolute right-[24px] top-[24px] flex items-center gap-2'>
          <CloseButton onClick={onClose} />
        </div>

        <div className='mx-4'>
          {loading && (
            <div className='flex items-center justify-center py-10'>
              <CircularProgress size={28} />
            </div>
          )}

          {!loading && error && (
            <div className='py-6 text-center text-red-600'>Failed to load conversion actions: {error.message}</div>
          )}

          {!loading && !error && ignoredFilterNames.length > 0 && (
            <p className='mb-3 text-xs text-[#84818A]'>
              {ignoredFilterNames.join(', ')} {ignoredFilterNames.length > 1 ? 'filters don’t' : 'filter doesn’t'} apply
              here — {dspLabel('beeswax')}'s conversion data doesn't carry that alongside the action name, so this
              breakdown covers every {ignoredFilterNames.map(name => name.toLowerCase()).join('/')} for the account, not
              just the selected one(s).
            </p>
          )}

          {!loading && !error && rows.length === 0 && (
            <div className='py-6 text-center text-[#84818A]'>
              No conversion actions recorded for the selected filters.
            </div>
          )}

          {!loading && !error && rows.length > 0 && (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Conversion action</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align='right'>
                      Fires
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(row => (
                    <TableRow key={row.eventName}>
                      <TableCell>{row.eventName}</TableCell>
                      <TableCell align='right'>{row.fires.toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                  {total !== null && (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }}>Total</TableCell>
                      <TableCell sx={{ fontWeight: 600, borderBottom: 'none' }} align='right'>
                        {total.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

export default ConversionBreakdownModal;
